import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, ImagePlus } from "lucide-react";
import {
  ACQUISITION_TYPES, CONDITIONS, ITEM_CATEGORIES, SEASONS,
  LocationRecord, itemsApi, locationsApi, taxonomyApi,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SearchSelect } from "@/components/catalog/OptionPicker";
import { ItemAttributeFields } from "@/components/catalog/ItemAttributeFields";
import { flat } from "@/lib/item-options";
import type { Item } from "@/lib/catalog-api";

const Field = ({ label, children, hint }: any) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-[0.18em]">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const Select = (props: any) => (
  <select {...props} className={`h-10 w-full border border-border bg-background px-3 text-sm ${props.className || ""}`} />
);

// A real dropdown of existing values with an "Add new…" option (mobile-friendly).
function ComboField({ label, hint, value, options, onChange, testid, required }: {
  label: string; hint?: string; value: string; options: string[];
  onChange: (v: string) => void; testid: string; required?: boolean;
}) {
  const [adding, setAdding] = useState(options.length === 0);
  useEffect(() => { setAdding(options.length === 0); }, [options.length]);

  if (adding) {
    return (
      <Field label={label} hint={hint}>
        <Input data-testid={testid} value={value} required={required}
          onChange={(e: any) => onChange(e.target.value)} placeholder="Type a new value" />
        {options.length > 0 && (
          <button type="button" className="text-[11px] text-oxblood mt-1"
            onClick={() => { setAdding(false); onChange(""); }}>← choose from list</button>
        )}
      </Field>
    );
  }
  return (
    <Field label={label} hint={hint}>
      <Select data-testid={testid} value={options.includes(value) ? value : ""} required={required}
        onChange={(e: any) => {
          if (e.target.value === "__add__") { setAdding(true); onChange(""); }
          else onChange(e.target.value);
        }}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        <option value="__add__">＋ Add new…</option>
      </Select>
    </Field>
  );
}


export default function ItemNew() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationRecord[]>([]);

  // Form state
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("Other");
  const [subCategory, setSubCategory] = useState("");
  const [attrs, setAttrs] = useState<Partial<Item>>({});
  const [condition, setCondition] = useState("Good");
  const [acquisition, setAcquisition] = useState("Owned");
  const [season, setSeason] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationType, setLocationType] = useState("");
  const [locationTypeOptions, setLocationTypeOptions] = useState<string[]>([]);
  const [retailValue, setRetailValue] = useState("");
  const [insuredValue, setInsuredValue] = useState("");
  const [tags, setTags] = useState("");
  const [notes, setNotes] = useState("");
  const [sku, setSku] = useState("");
  const [box, setBox] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<string[]>(ITEM_CATEGORIES as unknown as string[]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const cameraRef = useRef<HTMLInputElement>(null);

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const files = Array.from(list);  // copy now — the input value is reset right after onChange
    setPhotoFiles((prev) => [...prev, ...files].slice(0, 10));
  }

  useEffect(() => {
    locationsApi.list().then(setLocations).catch(() => {});
    taxonomyApi.categories().then((r) => setCategoryOptions(r.categories)).catch(() => {});
    taxonomyApi.brands().then((r) => setBrandOptions(r.brands)).catch(() => {});
    taxonomyApi.locationTypes().then((r) => setLocationTypeOptions(r.location_types)).catch(() => {});
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const item = await itemsApi.create({
        name: name.trim(),
        brand: brand.trim() || undefined,
        category: category as any,
        sub_category: subCategory.trim() || undefined,
        ...attrs,
        condition: condition as any,
        acquisition_type: acquisition as any,
        season: (season || undefined) as any,
        current_location_name: locationName.trim() || undefined,
        location_type: locationType.trim() || undefined,
        retail_value: retailValue ? Number(retailValue) : undefined,
        insured_value: insuredValue ? Number(insuredValue) : undefined,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || undefined,
        sku: sku.trim() || undefined,
        box: box.trim() || undefined,
      });
      // Upload photos sequentially (so primary order is stable: first uploaded becomes primary)
      let uploaded = 0, failed = 0;
      for (const f of photoFiles) {
        try { await itemsApi.uploadPhoto(item._id, f); uploaded++; }
        catch { failed++; }
      }
      if (failed > 0) {
        toast.error(`${uploaded} photo${uploaded === 1 ? "" : "s"} uploaded, ${failed} failed. You can re-add the rest on the item page.`);
      } else if (uploaded > 0) {
        toast.success(`Item saved with ${uploaded} photo${uploaded === 1 ? "" : "s"}.`);
      }
      navigate(`/app/items/${item._id}?new=1`);
    } catch (e: any) {
      setError(e?.message || "Could not create item.");
    } finally { setSubmitting(false); }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="item-new-page">
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-oxblood inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <h1 className="font-display text-3xl sm:text-4xl">Catalog a new item</h1>
      <p className="text-sm text-muted-foreground mt-2">Photos can be added now or after — you can also add them later from the item page.</p>

      <form onSubmit={onSubmit} data-testid="item-new-form" className="mt-8 grid lg:grid-cols-[1fr_1.5fr] gap-10 max-w-5xl">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Photos</p>
          {/* Camera-first capture (opens the camera directly on phones) */}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.currentTarget.value = ""; }}
            data-testid="item-new-camera" />
          <Button type="button" onClick={() => cameraRef.current?.click()}
            data-testid="item-new-take-photo"
            className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12 text-sm mb-1">
            <Camera className="w-4 h-4 mr-2" /> {photoFiles.length > 0 ? "Take another photo" : "Take photo"}
          </Button>
          <p className="text-[11px] text-muted-foreground mb-3 text-center">Snap several in a row — each one is added below before you save.</p>
          <label className="border border-dashed border-border aspect-[4/5] grid place-items-center cursor-pointer hover:border-oxblood text-sm text-muted-foreground hover:text-oxblood"
            data-testid="item-new-photo-drop">
            <input type="file" multiple accept="image/*" className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.currentTarget.value = ""; }}
              data-testid="item-new-photos" />
            <div className="text-center px-4">
              <ImagePlus className="w-6 h-6 mx-auto mb-2 opacity-70" strokeWidth={1.5} />
              <p className="font-display text-xl">Add from library</p>
              <p className="text-xs mt-1">tap to choose, or drop photos here</p>
              <p className="text-[11px] mt-3">{photoFiles.length} selected · max 10 · 8MB each</p>
            </div>
          </label>
          {photoFiles.length > 0 && (
            <ul className="mt-3 grid grid-cols-3 gap-2" data-testid="item-new-photo-list">
              {photoFiles.slice(0, 10).map((f, i) => (
                <li key={i} className="relative group aspect-square border border-border overflow-hidden bg-bone">
                  <img src={URL.createObjectURL(f)} alt={f.name || `Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" aria-label="Remove photo"
                    className="absolute top-1 right-1 w-6 h-6 grid place-items-center bg-charcoal/70 text-ivory text-xs rounded-full"
                    onClick={() => setPhotoFiles((prev) => prev.filter((_, idx) => idx !== i))}>×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-8">
          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Identification</p>
            <Field label="Name *">
              <Input data-testid="item-new-name" value={name} onChange={(e: any) => setName(e.target.value)} required maxLength={200} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Brand" hint="Search, or type to add a new brand">
                <SearchSelect testid="item-new-brand" value={brand} allowCustom placeholder="Select brand"
                  groups={flat(brandOptions)} onChange={(v) => setBrand(v || "")} />
              </Field>
              <Field label="Category *" hint="Search, or type to add a new category">
                <SearchSelect testid="item-new-category" value={category} allowCustom placeholder="Select category"
                  groups={flat(categoryOptions)} onChange={(v) => setCategory(v || "Other")} />
              </Field>
              <Field label="Sub-category"><Input data-testid="item-new-subcategory" value={subCategory} onChange={(e: any) => setSubCategory(e.target.value)} /></Field>
              <Field label="Season">
                <Select data-testid="item-new-season" value={season} onChange={(e: any) => setSeason(e.target.value)}>
                  <option value="">—</option>
                  {SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Details</p>
            <ItemAttributeFields prefix="item-new" largeLabels value={attrs} category={category}
              onPatch={(p) => setAttrs((a) => ({ ...a, ...p }))} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Condition *">
                <Select data-testid="item-new-condition" value={condition} onChange={(e: any) => setCondition(e.target.value)}>
                  {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Acquisition">
                <Select data-testid="item-new-acquisition" value={acquisition} onChange={(e: any) => setAcquisition(e.target.value)}>
                  {ACQUISITION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Assignment</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <ComboField label="Current location" hint="Pick a location or add new" testid="item-new-location"
                value={locationName} options={locations.map((l) => l.name)} onChange={setLocationName} />
              <ComboField label="Location type" hint="e.g. Rack, Main Closet — pick or add new" testid="item-new-location-type"
                value={locationType} options={locationTypeOptions} onChange={setLocationType} />
            </div>
          </section>

          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Value</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Retail value (USD)"><Input type="number" data-testid="item-new-retail" value={retailValue} onChange={(e: any) => setRetailValue(e.target.value)} /></Field>
              <Field label="Insured value (USD)"><Input type="number" data-testid="item-new-insured" value={insuredValue} onChange={(e: any) => setInsuredValue(e.target.value)} /></Field>
            </div>
          </section>

          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Codes & storage</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="SKU" hint="Leave blank to auto-generate (e.g. MIA-CHA-DR-1)">
                <Input data-testid="item-new-sku" value={sku} onChange={(e: any) => setSku(e.target.value)} maxLength={80} />
              </Field>
              <Field label="Box" hint="Box label the piece lives in, e.g. CHA3">
                <Input data-testid="item-new-box" value={box} onChange={(e: any) => setBox(e.target.value)} maxLength={80} />
              </Field>
            </div>
          </section>

          <section className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood font-medium">— Tags & notes</p>
            <Field label="Tags" hint="Comma-separated, e.g. press, Cannes, hold">
              <Input data-testid="item-new-tags" value={tags} onChange={(e: any) => setTags(e.target.value)} />
            </Field>
            <Field label="Notes">
              <textarea data-testid="item-new-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-border bg-background p-3 text-sm" />
            </Field>
          </section>

          {error && <p data-testid="item-new-error" role="alert" className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2">{error}</p>}

          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" data-testid="item-new-submit" disabled={!name.trim() || submitting}
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12 px-7">
              {submitting ? "Cataloging…" : "Catalog item"}
            </Button>
            <Button type="button" onClick={() => navigate(-1)} variant="ghost" className="h-12 px-5">Cancel</Button>
          </div>
        </div>
      </form>
    </main>
  );
}
